import { useState } from 'react';
import clsx from 'clsx';
import type { DagData } from '../../stores/controle-store';

interface DagRijProps {
  dag: DagData;
  isExpanded: boolean;
  isReadOnly: boolean;
  onToggle: () => void;
  onBankChange: (datum: string, bedrag: number) => void;
  onContantChange: (datum: string, bedrag: number) => void;
  onStatusToggle: (datum: string) => void;
}

function formatAmount(amount: number | null): string {
  if (amount === null || amount === 0) return '-';
  return new Intl.NumberFormat('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
}

function verschilCellClass(verschil: number, hasExtern: boolean): string {
  if (!hasExtern) return 'text-gray-300';
  const abs = Math.abs(verschil);
  if (abs > 1) return 'bg-red-50 text-red-700 font-semibold';
  if (abs > 0) return 'text-amber-600';
  return 'text-green-600';
}

function formatDayLabel(datum: string): string {
  const d = new Date(datum + 'T12:00:00');
  const day = d.getDate();
  const month = d.getMonth() + 1;
  const weekday = d.toLocaleDateString('nl-NL', { weekday: 'short' });
  return `${weekday} ${day}-${month}`;
}

function isWeekend(datum: string): boolean {
  const d = new Date(datum + 'T12:00:00');
  return d.getDay() === 0 || d.getDay() === 6;
}

export function DagRij({ dag, isExpanded, isReadOnly, onToggle, onBankChange, onContantChange, onStatusToggle }: DagRijProps) {
  const [editingBank, setEditingBank] = useState(false);
  const [bankValue, setBankValue] = useState('');
  const [editingContant, setEditingContant] = useState(false);
  const [contantValue, setContantValue] = useState('');

  const weekend = isWeekend(dag.datum);
  const hasActivity = dag.sfCount > 0 || dag.mollieCount > 0 || dag.myposCount > 0;
  const dagStatus = dag.notitie?.status || 'open';

  const handleBankSubmit = () => {
    const val = parseFloat(bankValue.replace(',', '.'));
    if (!isNaN(val)) {
      onBankChange(dag.datum, val);
    }
    setEditingBank(false);
  };

  const handleContantSubmit = () => {
    const val = parseFloat(contantValue.replace(',', '.'));
    if (!isNaN(val)) {
      onContantChange(dag.datum, val);
    }
    setEditingContant(false);
  };

  const handleContantClick = () => {
    if (isReadOnly) return;
    if (dag.contant.envelop !== null) {
      // Already has a value — edit it
      setContantValue(dag.contant.envelop.toString());
    } else {
      // First click: auto-fill with geregistreerd amount (one-click confirm)
      onContantChange(dag.datum, dag.contant.geregistreerd);
      return;
    }
    setEditingContant(true);
  };

  return (
    <tr
      className={clsx(
        'border-b border-gray-50 text-sm',
        weekend && 'bg-gray-50/50',
        isExpanded && 'bg-blue-50/30',
        hasActivity && !isReadOnly && 'cursor-pointer',
      )}
      onClick={hasActivity ? onToggle : undefined}
    >
      {/* Status indicator + Datum */}
      <td className="py-1.5 px-2 whitespace-nowrap">
        <div className="flex items-center gap-1.5">
          {/* Status circle */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (!isReadOnly && hasActivity) onStatusToggle(dag.datum);
            }}
            className={clsx(
              'w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0',
              dagStatus === 'verklaard' && 'bg-emerald-100 border-emerald-400',
              dagStatus === 'open' && hasActivity && 'border-gray-300 hover:border-gray-400',
              dagStatus === 'open' && !hasActivity && 'border-gray-200',
            )}
            disabled={isReadOnly || !hasActivity}
          >
            {dagStatus === 'verklaard' && (
              <svg className="w-2.5 h-2.5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>
          <span className="text-muted font-mono text-xs">{formatDayLabel(dag.datum)}</span>
        </div>
      </td>

      {/* Pin / MyPos */}
      <td className="py-1.5 px-2 text-right tabular-nums">{dag.pin.extern !== null ? formatAmount(dag.pin.extern) : <span className="text-gray-300">-</span>}</td>
      <td className="py-1.5 px-2 text-right tabular-nums">{formatAmount(dag.pin.sf)}</td>
      <td className={clsx('py-1.5 px-2 text-right tabular-nums', verschilCellClass(dag.pin.verschil, dag.pin.extern !== null))}>
        {dag.pin.extern !== null ? formatAmount(dag.pin.verschil) : <span className="text-gray-300">-</span>}
      </td>

      <td className="w-px bg-gray-100" />

      {/* iDEAL / Mollie */}
      <td className="py-1.5 px-2 text-right tabular-nums">{formatAmount(dag.ideal.extern)}</td>
      <td className="py-1.5 px-2 text-right tabular-nums">{formatAmount(dag.ideal.sf)}</td>
      <td className={clsx('py-1.5 px-2 text-right tabular-nums', verschilCellClass(dag.ideal.verschil, dag.ideal.extern !== null))}>
        {dag.ideal.extern !== null ? formatAmount(dag.ideal.verschil) : '-'}
      </td>

      <td className="w-px bg-gray-100" />

      {/* Bank */}
      <td className="py-1.5 px-2 text-right" onClick={(e) => e.stopPropagation()}>
        {editingBank && !isReadOnly ? (
          <input
            type="text"
            value={bankValue}
            onChange={(e) => setBankValue(e.target.value)}
            onBlur={handleBankSubmit}
            onKeyDown={(e) => e.key === 'Enter' && handleBankSubmit()}
            className="w-20 text-right text-sm border border-gray-300 rounded px-1 py-0.5"
            autoFocus
          />
        ) : (
          <button
            onClick={() => {
              if (!isReadOnly) {
                setBankValue(dag.bank.extern?.toString() || '');
                setEditingBank(true);
              }
            }}
            className={clsx(
              'text-right tabular-nums px-1 rounded min-w-[60px] inline-block',
              !isReadOnly && 'hover:bg-blue-50 hover:text-blue-700 cursor-text',
            )}
            disabled={isReadOnly}
          >
            {formatAmount(dag.bank.extern)}
          </button>
        )}
      </td>
      <td className="py-1.5 px-2 text-right tabular-nums">{formatAmount(dag.bank.sf)}</td>
      <td className={clsx('py-1.5 px-2 text-right tabular-nums', verschilCellClass(dag.bank.verschil, dag.bank.extern !== null))}>
        {dag.bank.extern !== null ? formatAmount(dag.bank.verschil) : '-'}
      </td>

      <td className="w-px bg-gray-100" />

      {/* Contant */}
      <td className="py-1.5 px-2 text-right tabular-nums">{formatAmount(dag.contant.geregistreerd)}</td>
      <td className="py-1.5 px-2 text-right tabular-nums">{formatAmount(dag.contant.gecontroleerd)}</td>
      <td className="py-1.5 px-2 text-right" onClick={(e) => e.stopPropagation()}>
        {editingContant && !isReadOnly ? (
          <input
            type="text"
            value={contantValue}
            onChange={(e) => setContantValue(e.target.value)}
            onBlur={handleContantSubmit}
            onKeyDown={(e) => e.key === 'Enter' && handleContantSubmit()}
            className="w-20 text-right text-sm border border-gray-300 rounded px-1 py-0.5"
            autoFocus
          />
        ) : (
          <button
            onClick={handleContantClick}
            className={clsx(
              'text-right tabular-nums px-1 rounded min-w-[60px] inline-block',
              dag.contant.envelop !== null && 'text-emerald-700',
              dag.contant.envelop === null && dag.contant.geregistreerd > 0 && !isReadOnly && 'text-gray-300 hover:bg-emerald-50 hover:text-emerald-600 cursor-pointer',
              dag.contant.envelop === null && dag.contant.geregistreerd === 0 && 'text-gray-300',
              !isReadOnly && dag.contant.envelop !== null && 'hover:bg-blue-50 cursor-text',
            )}
            disabled={isReadOnly || dag.contant.geregistreerd === 0}
          >
            {dag.contant.envelop !== null ? formatAmount(dag.contant.envelop) : (dag.contant.geregistreerd > 0 ? '✓' : '-')}
          </button>
        )}
      </td>
      <td className={clsx('py-1.5 px-2 text-right tabular-nums', verschilCellClass(dag.contant.verschil, dag.contant.envelop !== null))}>
        {dag.contant.envelop !== null ? formatAmount(dag.contant.verschil) : '-'}
      </td>
    </tr>
  );
}
